import { useTableState } from '@driftt/dds'
import { pathOr } from 'ramda'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

import {
  getAllContentVariables,
  getAllOrgContentVariables,
  getContentVariableUsage,
} from 'api/playbooks'
import { isEmptyOrNil } from 'utils/index'

export const useContentVariables = ({
  nameQuery = '',
  direction = 'asc',
  sortProperty = 'updated_at',
}) => {
  const { tableState, handleTableStateChange } = useTableState({
    defaultPagination: {
      pageIndex: 0,
      pageSize: 10,
    },
  })
  const {
    isLoading,
    data: variables,
    refetch: refreshTableState,
  } = useQuery({
    queryKey: [
      `playbook-content-variables--all-${tableState.pagination.pageIndex}-${tableState.pagination.pageSize}`,
      nameQuery,
    ],
    queryFn: () =>
      getAllContentVariables(
        nameQuery,
        tableState.pagination.pageSize,
        tableState.pagination.pageIndex,
        direction,
        sortProperty
      ),
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
    staleTime: Infinity,
    retry: false,
  })

  const memoizedVariables = useMemo(() => variables, [variables])

  const memoizedRowTotal = useMemo(
    () => (isEmptyOrNil(variables) ? 0 : variables.meta.total),
    [variables]
  )

  return {
    variables: memoizedVariables,
    isLoading,
    tableStateChange: handleTableStateChange,
    pagination: tableState.pagination,
    rowTotal: memoizedRowTotal,
    refreshTableState,
  }
}

export const useAllOrgContentVariablesTable = ({
  nameQuery = null,
  pageSize = null,
  page = 0,
  direction = 'asc',
  sortProperty = null,
}) => {
  const { isLoading, data: variables } = useQuery({
    queryKey: [`playbook-content-variables--all-org`],
    queryFn: () =>
      getAllOrgContentVariables(
        nameQuery,
        pageSize,
        page,
        direction,
        sortProperty
      ),
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
    cacheTime: 500,
    staleTime: 500,
    retry: 0,
  })

  const memoizedVariables = useMemo(() => variables, [variables])

  return {
    variables: memoizedVariables,
    isLoading,
  }
}

export const useContentVariableUsage = (variableId) => {
  const { isLoading, data: usages } = useQuery({
    queryFn: () => getContentVariableUsage(variableId),
    queryKey: ['content-variable', variableId, 'usage'],
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
    cacheTime: 500,
    staleTime: 500,
    retry: 0,
  })

  const memoizedUsages = useMemo(
    () => pathOr([], ['contentVariableUsagesContextList'], usages),
    [usages]
  )

  return {
    usages: memoizedUsages,
    isLoading,
  }
}

export const useAllOrgContentVariables = () => {
  const { isLoading, data: variables } = useQuery({
    queryKey: [`playbook-content-variables--all-org`],
    queryFn: () => getAllOrgContentVariables(),
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
    cacheTime: 500,
    staleTime: 500,
    retry: 0,
  })

  const memoizedVariables = useMemo(() => variables, [variables])

  return {
    variables: memoizedVariables,
    isLoading,
  }
}
